import React, { useState, useRef, useEffect } from 'react';
import { chatbotAPI } from '../../services/api';
import { 
  MessageCircle, 
  X, 
  Send, 
  User, 
  Bot, 
  Loader2,
  Minimize2,
  Maximize2
} from 'lucide-react';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '', company: '' });
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize chat session
  const initializeSession = async () => {
    try {
      const response = await chatbotAPI.createSession({
        pageUrl: window.location.href,
        referrer: document.referrer
      });
      
      setSessionId(response.data.sessionId);
      setMessages([{ role: 'bot', content: response.data.message }]);
      setQuickReplies(response.data.quickReplies);
    } catch (error) {
      console.error('Error initializing chat:', error);
      setMessages([{ 
        role: 'bot', 
        content: 'Hello! Welcome to TeqDeepSeek Solutions. How can I help you today?' 
      }]);
    }
  };

  // Open chat
  const handleOpen = () => {
    setIsOpen(true);
    if (!sessionId) {
      initializeSession();
    }
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Send message
  const handleSendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || !sessionId) return;

    const userMessage = messageText.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    setQuickReplies([]);

    try {
      const response = await chatbotAPI.sendMessage({
        sessionId,
        message: userMessage
      });

      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: response.data.message 
      }]);
      
      if (response.data.quickReplies?.length > 0) {
        setQuickReplies(response.data.quickReplies);
      }

      // Check if should show lead form
      if (response.data.message.toLowerCase().includes('contact you') || 
          response.data.message.toLowerCase().includes('details')) {
        setTimeout(() => setShowLeadForm(true), 1000);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: 'I apologize, but I\'m having trouble processing your request. Please try again or contact us directly.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle quick reply click
  const handleQuickReply = (reply) => {
    handleSendMessage(reply);
  };

  // Submit lead form
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadData.name || !leadData.email) return;

    try {
      await chatbotAPI.convertToLead({
        sessionId,
        ...leadData
      });

      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: 'Thank you! We\'ve received your information and will contact you within 24 hours.' 
      }]);
      
      setShowLeadForm(false);
      setLeadData({ name: '', email: '', phone: '', company: '' });
    } catch (error) {
      console.error('Error submitting lead:', error);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-accent hover:bg-accent-light text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
      >
        <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal rounded-full animate-pulse"></span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 ${
        isMinimized ? 'w-72 h-14' : 'w-96 h-[500px]'
      }`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-accent px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">TeqDeepSeek Assistant</h3>
            <p className="text-white/70 text-xs">Online</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="h-[360px] overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-2 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.role === 'user' 
                    ? 'bg-accent' 
                    : 'bg-gradient-to-br from-primary to-accent'
                }`}>
                  {message.role === 'user' ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Bot className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                  message.role === 'user'
                    ? 'bg-accent text-white rounded-br-md'
                    : 'bg-white text-slate-700 shadow-sm rounded-bl-md'
                }`}>
                  {message.content.split('\n').map((line, i) => (
                    <p key={i} className={i > 0 ? 'mt-1' : ''}>
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                  <Loader2 className="w-5 h-5 text-accent animate-spin" />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {quickReplies.length > 0 && (
            <div className="px-4 py-2 bg-white border-t border-slate-100">
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickReply(reply)}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-accent hover:text-white text-slate-600 text-xs rounded-full transition-colors"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Lead Form */}
          {showLeadForm && (
            <div className="px-4 py-3 bg-white border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-2">Please share your details:</p>
              <form onSubmit={handleLeadSubmit} className="space-y-2">
                <input
                  type="text"
                  placeholder="Your name"
                  value={leadData.name}
                  onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-accent focus:outline-none"
                  required
                />
                <input
                  type="email"
                  placeholder="Email address"
                  value={leadData.email}
                  onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-accent focus:outline-none"
                  required
                />
                <div className="flex gap-2">
                  <input
                    type="tel"
                    placeholder="Phone (optional)"
                    value={leadData.phone}
                    onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })}
                    className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-accent focus:outline-none"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-accent text-white text-sm rounded-lg hover:bg-accent-light transition-colors"
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-white border-t border-slate-100">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2.5 border border-slate-200 rounded-full text-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim() || isLoading}
                className="w-10 h-10 bg-accent text-white rounded-full flex items-center justify-center hover:bg-accent-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Chatbot;
